import { Component, HostListener, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleSortState,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { ManageRolesTableComponent } from './manage-roles-table/manage-roles-table.component';
import {
  RoleApiService,
  type RoleListMeta,
  type RoleRow,
} from 'src/app/core/services/role-api.service';
import { RbacPortalScopeService } from 'src/app/core/services/rbac-portal-scope.service';
import { isSuperAdminRoleSlug } from 'src/app/core/constants/super-admin-role';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import {
  DeleteConfirmModalComponent,
  type DeleteConfirmVariant,
} from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';

export type RoleListView = 'active' | 'deleted';

@Component({
  selector: 'app-manage-roles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    ListModuleSurfaceComponent,
    ManageRolesTableComponent,
    HasPermissionPipe,
    RestoreConfirmModalComponent,
    DeleteConfirmModalComponent,
  ],
  templateUrl: './manage-roles.component.html',
  styleUrl: './manage-roles.component.css',
})
export class ManageRolesComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly roleApi = inject(RoleApiService);
  readonly rbacScope = inject(RbacPortalScopeService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  view: RoleListView = 'active';
  roles: RoleRow[] = [];
  loading = false;
  errorMessage = '';

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search role name or slug…',
        deletedPlaceholder: 'Search deleted roles…',
      },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  page = 1;
  readonly pageSize = 10;
  meta: RoleListMeta | null = null;

  sortColumn: 'role_name' | 'slug' | 'description' | 'created_at' | 'deleted_at' = 'role_name';
  sortBy: 'asc' | 'desc' = 'asc';

  openActionMenuKey: string | null = null;

  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  deleteVariant: DeleteConfirmVariant = 'soft-delete';
  showRestoreModal = false;
  addLoading = signal(false);
  editLoading = signal(false);
  deleteLoading = signal(false);
  restoreLoading = signal(false);
  modalError = signal<string | null>(null);

  selectedRole: RoleRow | null = null;

  addForm: FormGroup = this.fb.group({
    role_name: ['', [Validators.required]],
    slug: ['', [Validators.required]],
    description: [''],
  });

  editForm: FormGroup = this.fb.group({
    role_name: ['', [Validators.required]],
    slug: ['', [Validators.required]],
    description: [''],
  });

  constructor() {
    effect(() => {
      this.rbacScope.scope();
      this.page = 1;
      this.loadRoles();
    });
  }

  setView(mode: RoleListView): void {
    if (this.view === mode) return;
    this.closeActionMenu();
    this.closeRestoreModal();
    this.closeDeleteModal();
    this.view = mode;
    this.page = 1;
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    if (mode === 'deleted') {
      this.sortColumn = 'deleted_at';
      this.sortBy = 'desc';
    } else {
      this.sortColumn = 'role_name';
      this.sortBy = 'asc';
    }
    this.loadRoles();
  }

  loadRoles(): void {
    this.loading = true;
    this.errorMessage = '';
    this.roleApi
      .listRoles({
        page: this.page,
        page_size: this.pageSize,
        searchText: this.listFilters.searchQuery.trim() || undefined,
        status: this.view === 'deleted' ? 'deleted' : 'active',
        sortColumn: this.sortColumnForApi(),
        sortBy: this.sortBy === 'asc' ? 'ASC' : 'DESC',
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          if (!res?.status || !res.data) {
            this.errorMessage = res?.message || 'Could not load roles.';
            this.roles = [];
            this.meta = null;
            return;
          }
          this.roles = res.data.result ?? [];
          this.meta = res.data.meta ?? null;
        },
        error: (err) => {
          this.roles = [];
          this.meta = null;
          this.errorMessage = this.parseHttpError(err);
        },
      });
  }

  get emptyMessage(): string {
    return this.view === 'deleted'
      ? 'No deleted roles match your filters.'
      : 'No roles match your filters.';
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.loadRoles();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.loadRoles();
  }

  onTableSort(state: ListModuleSortState): void {
    const column = state.column as typeof this.sortColumn;
    if (this.view === 'deleted') {
      if (column !== 'role_name' && column !== 'slug' && column !== 'description' && column !== 'deleted_at') {
        return;
      }
    } else if (column !== 'role_name' && column !== 'slug' && column !== 'description' && column !== 'created_at') {
      return;
    }
    this.sortColumn = column;
    this.sortBy = state.direction;
    this.page = 1;
    this.loadRoles();
  }

  menuManagePermissions(role: RoleRow): void {
    this.closeActionMenu();
    this.goToManagePermissions(role);
  }

  menuEdit(role: RoleRow): void {
    this.closeActionMenu();
    this.openEditModal(role);
  }

  menuDelete(role: RoleRow): void {
    this.closeActionMenu();
    this.confirmDelete(role);
  }

  menuRestore(role: RoleRow): void {
    this.closeActionMenu();
    this.confirmRestore(role);
  }

  menuToggleStatus(role: RoleRow): void {
    this.closeActionMenu();
    this.toggleStatus(role);
  }

  private sortColumnForApi(): 'role_name' | 'slug' | 'description' | 'created_at' | 'deleted_at' {
    if (this.view === 'deleted' && this.sortColumn === 'created_at') {
      return 'deleted_at';
    }
    return this.sortColumn;
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.agent-action-menu')) {
      this.closeActionMenu();
    }
  }

  openAddModal(): void {
    this.addForm.reset();
    this.modalError.set(null);
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
  }

  onAddRoleNameChange(): void {
    const name = this.addForm.get('role_name')?.value ?? '';
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_+|_+$)/g, '');
    this.addForm.patchValue({ slug }, { emitEvent: false });
  }

  submitAdd(): void {
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      return;
    }
    this.modalError.set(null);
    this.addLoading.set(true);
    const v = this.addForm.value;
    this.roleApi
      .createRole({
        role_name: v.role_name,
        slug: v.slug,
        description: v.description || undefined,
      })
      .pipe(finalize(() => this.addLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status) {
            this.closeAddModal();
            this.showToast(res.message || 'Role created.');
            this.loadRoles();
          } else {
            this.modalError.set(res?.message || 'Failed to create role.');
          }
        },
        error: (e) => this.modalError.set(this.parseHttpError(e)),
      });
  }

  isProtectedRole(role: RoleRow): boolean {
    return isSuperAdminRoleSlug(role.slug);
  }

  openEditModal(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    this.closeActionMenu();
    this.selectedRole = role;
    this.editForm.patchValue({
      role_name: role.role_name,
      slug: role.slug,
      description: role.description ?? '',
    });
    this.modalError.set(null);
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedRole = null;
  }

  submitEdit(): void {
    if (this.editForm.invalid || !this.selectedRole) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.modalError.set(null);
    this.editLoading.set(true);
    const v = this.editForm.value;
    this.roleApi
      .updateRole(this.selectedRole.id, {
        role_name: v.role_name,
        slug: v.slug,
        description: v.description || undefined,
        is_active: this.selectedRole.is_active,
      })
      .pipe(finalize(() => this.editLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res?.status) {
            this.closeEditModal();
            this.showToast(res.message || 'Role updated.');
            this.loadRoles();
          } else {
            this.modalError.set(res?.message || 'Failed to update role.');
          }
        },
        error: (e) => this.modalError.set(this.parseHttpError(e)),
      });
  }

  confirmDelete(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    this.closeActionMenu();
    this.modalError.set(null);
    this.deleteVariant = 'soft-delete';
    this.selectedRole = role;
    this.showDeleteModal = true;
  }

  confirmPermanentDelete(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    this.closeActionMenu();
    this.modalError.set(null);
    this.deleteVariant = 'permanent';
    this.selectedRole = role;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedRole = null;
    this.modalError.set(null);
  }

  submitDelete(): void {
    if (!this.selectedRole) return;
    if (this.deleteVariant === 'permanent') {
      this.runPermanentDelete();
      return;
    }
    this.modalError.set(null);
    this.deleteLoading.set(true);
    this.roleApi
      .deleteRole(this.selectedRole.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.closeDeleteModal();
          this.showToast(res?.message || 'Role deleted.');
          this.loadRoles();
        },
        error: (e) => this.modalError.set(this.parseHttpError(e)),
      });
  }

  private runPermanentDelete(): void {
    if (!this.selectedRole) return;
    this.modalError.set(null);
    this.deleteLoading.set(true);
    this.roleApi
      .permanentDeleteRole(this.selectedRole.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.closeDeleteModal();
          this.showToast(res?.message || 'Role permanently deleted.');
          if (this.roles.length <= 1 && this.page > 1) {
            this.page -= 1;
          }
          this.loadRoles();
        },
        error: (e) => this.modalError.set(this.parseHttpError(e)),
      });
  }

  get deleteModalHeading(): string {
    return this.deleteVariant === 'permanent' ? 'Delete role permanently?' : 'Remove role?';
  }

  confirmRestore(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    this.closeActionMenu();
    this.selectedRole = role;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedRole = null;
  }

  submitRestore(): void {
    if (!this.selectedRole) return;
    this.restoreLoading.set(true);
    this.roleApi
      .restoreRole(this.selectedRole.id)
      .pipe(finalize(() => this.restoreLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.closeRestoreModal();
          this.showToast(res?.message || 'Role restored.');
          this.loadRoles();
        },
        error: (e) => {
          this.closeRestoreModal();
          this.showToast(this.parseHttpError(e), 'error');
        },
      });
  }

  toggleStatus(role: RoleRow): void {
    if (this.view !== 'active' || this.isProtectedRole(role)) return;
    this.roleApi.toggleRoleStatus(role.id).subscribe({
      next: (res) => {
        if (res?.data) {
          role.is_active = res.data.is_active;
        } else {
          role.is_active = !role.is_active;
        }
        this.showToast(res?.message || 'Status updated.');
      },
      error: (e) => this.showToast(this.parseHttpError(e), 'error'),
    });
  }

  goToManagePermissions(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    void this.router.navigate(['/system-preferences/manage-permissions', role.id], {
      state: { role },
    });
  }

  private showToast(message: string, type: 'success' | 'error' = 'success'): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: type === 'error' ? 'snackbar-error' : undefined,
    });
  }

  private parseHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message: unknown }).message;
        if (typeof m === 'string') return m;
        if (Array.isArray(m)) return m.join(', ');
      }
      return err.message || 'Request failed';
    }
    if (err instanceof Error) return err.message;
    return 'Request failed';
  }
}
